import React, { useEffect, useState } from 'react';
import { Modal, Button, Badge, Alert, Spinner, Table } from 'react-bootstrap';
import CopyValue from './CopyValue';
import { STATUS_VARIANTS, NEXT_ACTION_LABELS, formatDate, subsOf } from './transferFormat';
import { describeError, fetchAppleTransferUser, fakeAppleTransferUser } from '../../services/appleTransferApi';

const Field = ({ label, children }) => (
  <>
    <dt className="apple-transfer-detail__label">{label}</dt>
    <dd className="apple-transfer-detail__value">{children}</dd>
  </>
);

// OC-622: everything the API knows about one Apple user, with copy buttons for the long values.
// `row` is the list row, shown at once; GET /users/{id} then adds the user's own actions.
const AppleTransferUserDetail = ({ row, fake, onHide }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!row) return undefined;
    let cancelled = false;
    setDetail(null);
    setError(null);
    setLoading(true);
    (fake ? Promise.resolve({ data: { data: fakeAppleTransferUser(row.id) } }) : fetchAppleTransferUser(row.id))
      .then((res) => {
        if (!cancelled) setDetail(res.data?.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(describeError(err, 'Failed to load the user'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row, fake]);

  const user = detail?.user || row;
  const actions = detail?.actions || [];
  const subs = subsOf(user);
  const status = user?.apple_migration_status;

  return (
    <Modal show={Boolean(row)} onHide={onHide} size="lg" centered scrollable className="apple-transfer-modal">
      <Modal.Header closeButton>
        <Modal.Title>Apple user {user?.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {user && (
          <dl className="apple-transfer-detail">
            <Field label="User ID"><CopyValue value={user.id} label="user ID" /></Field>
            <Field label="Email">
              <CopyValue value={user.email} label="email" code={false} />
              {user.is_private_email && <div className="small text-muted mt-1">Private relay address</div>}
            </Field>
            <Field label="Name">{user.name || <span className="text-muted">—</span>}</Field>
            <Field label="Team">
              <Badge
                bg={user.apple_sub_team === 'hk' ? 'success' : 'secondary'}
                className={`text-white apple-transfer-team ${user.apple_sub_team === 'hk' ? 'is-hk' : 'is-vi'}`}
              >
                {(user.apple_sub_team || '—').toUpperCase()}
              </Badge>
            </Field>
            <Field label="Apple sub (VI)"><CopyValue value={subs.vi} label="VI Apple sub" /></Field>
            <Field label="transfer_sub"><CopyValue value={user.apple_transfer_sub} label="transfer_sub" /></Field>
            <Field label="Apple sub (HK, after exchange)"><CopyValue value={subs.hk} label="HK Apple sub" /></Field>
            <Field label="Apple email (VI)"><CopyValue value={user.apple_email_vi} label="VI Apple email" code={false} /></Field>
            <Field label="Status">
              <Badge
                bg={STATUS_VARIANTS[status] || 'secondary'}
                className={status === 'conflict' ? 'text-dark' : 'text-white'}
              >
                {status || '—'}
              </Badge>
              {user.next_action && (
                <span className="small text-muted ms-2">Next: {NEXT_ACTION_LABELS[user.next_action] || user.next_action}</span>
              )}
            </Field>
            <Field label="Last error">
              {user.apple_migration_error
                ? <CopyValue value={user.apple_migration_error} label="last error" />
                : <span className="text-muted">—</span>}
            </Field>
            <Field label="Last run">{formatDate(user.apple_migration_attempted_at || user.apple_migration_at)}</Field>
            <Field label="Migrated at">{formatDate(user.apple_migrated_at)}</Field>
            <Field label="Runs for this user">
              {loading ? <Spinner animation="border" size="sm" /> : actions.length >= 20 ? '20+' : actions.length}
              <div className="small text-muted mt-1">Batch runs are logged per batch, not per user.</div>
            </Field>
          </dl>
        )}

        <h4 className="mt-3 mb-2">Actions for this user</h4>
        {loading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : actions.length === 0 ? (
          <p className="text-muted mb-0">No single-user actions yet.</p>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Time</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id}>
                    <td>{a.id}</td>
                    <td className="small text-nowrap">{formatDate(a.created_at)}</td>
                    <td>{a.admin_email || '—'}</td>
                    <td>{a.action}</td>
                    <td>{a.status || '—'}</td>
                    <td className="apple-transfer-detail__result">{a.result || a.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AppleTransferUserDetail;
